// src/utils/pricing.js
export function calculateShipping(subtotal, city, settings) {
  if (subtotal <= 0) return 0;

  const freeShippingEnabled = settings.free_shipping_enabled === '1';
  const freeShippingThreshold = Number(settings.free_shipping_threshold || 4000);
  if (freeShippingEnabled && subtotal >= freeShippingThreshold) {
    return 0;
  }

  const homeCity = (settings.shipping_home_city || '').trim().toLowerCase();
  const enteredCity = (city || '').trim().toLowerCase();
  const isHomeCity = homeCity && enteredCity && enteredCity === homeCity;

  if (isHomeCity) {
    return Number(settings.shipping_home_city_rate || 150);
  }
  return Number(settings.shipping_other_city_rate || 250);
}

// Validates an entered promo code against the single admin-configured
// code in StoreSettings.jsx, and computes the discount it produces for
// this specific cart. Returns { valid, discount, message }.
export function validatePromoCode(enteredCode, subtotal, cartItems, settings) {
  if (settings.promo_code_enabled !== '1') {
    return { valid: false, discount: 0, message: 'Promo codes are not currently active.' };
  }

  const savedCode = (settings.promo_code || '').trim().toLowerCase();
  const typedCode = (enteredCode || '').trim().toLowerCase();

  if (!savedCode || typedCode !== savedCode) {
    return { valid: false, discount: 0, message: 'Invalid promo code.' };
  }

  // Date range check — mirrors calculateFlatDiscount's logic
  const start = settings.promo_code_start_date;
  const end = settings.promo_code_end_date;
  const today = new Date().toISOString().slice(0, 10);
  if (start && today < start) {
    return { valid: false, discount: 0, message: 'This promo code is not active yet.' };
  }
  if (end && today > end) {
    return { valid: false, discount: 0, message: 'This promo code has expired.' };
  }

  const type = settings.promo_discount_type || 'percentage';
  const value = Number(settings.promo_discount_value || 0);
  let discount = 0;

  if (type === 'percentage') {
    discount = subtotal * (value / 100);
  } else if (type === 'fixed_cart') {
    discount = value;
  } else if (type === 'fixed_product') {
    const targetId = String(settings.promo_product_id || '');
    const matchingItem = (cartItems || []).find(item => String(item.id) === targetId);
    if (!matchingItem) {
      return { valid: false, discount: 0, message: "This code doesn't apply to any item in your cart." };
    }
    discount = Math.min(value, matchingItem.price * matchingItem.quantity);
  }

  discount = Math.min(Math.round(discount), subtotal);
  return { valid: true, discount, message: 'Promo code applied!' };
}

// Automatic sitewide percentage discount — no code required. Optionally
// restricted to a date range; runs indefinitely if both dates are blank.
export function calculateFlatDiscount(subtotal, settings) {
  if (subtotal <= 0) return 0;
  if (settings.flat_discount_enabled !== '1') return 0;

  const start = settings.flat_discount_start_date;
  const end = settings.flat_discount_end_date;
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD, matches <input type="date">

  if (start && today < start) return 0;
  if (end && today > end) return 0;

  const pct = Number(settings.flat_discount_percentage || 0);
  return Math.round(subtotal * (pct / 100));
}